import AsyncStorage from '@react-native-async-storage/async-storage';
import {SafeAuthError} from '../auth/authErrorPolicy';
import {clearPersistedActivePlayerConfirmation} from './activePlayerConfirmationStorage';
import {persistBayPlayerJwt} from './bayPlayerCredentialStorage';
import {track} from './analytics';

// Determines which live-session backend owns the authenticated session after
// reload. Legacy is adopted only for the explicit station-not-managed fallback.
export type SessionBackend = 'bay-session' | 'legacy';

export const SESSION_BACKEND_STORAGE_KEY = 'session_backend';
export const FULL_SWING_SESSION_ID_STORAGE_KEY = 'session_id';
export const DEFAULT_SESSION_BACKEND: SessionBackend = 'bay-session';

const SESSION_STORAGE_KEY = 'session';
const LAST_ACTIVE_TIME_STORAGE_KEY = 'lastActiveTime';

type FullSwingAuthPersistenceStage = 'session' | 'last-active-time' | 'backend' | 'credential';
export type FullSwingAuthCleanupOperation = FullSwingAuthPersistenceStage | 'session-id' | 'confirmation';

type PersistFullSwingAuthSessionParams = {
  serializedSession: string, lastActiveTime: string, backend: SessionBackend, bayPlayerJwt: string
};

export const FULL_SWING_AUTH_STORAGE_ERROR_MESSAGE = 'We could not save your session on this device. Please try again.';

class FullSwingAuthSessionStorageError extends SafeAuthError {
  readonly stage: Exclude<FullSwingAuthPersistenceStage, 'credential'>;

  constructor(stage: Exclude<FullSwingAuthPersistenceStage, 'credential'>) {
    super({
      category: 'device_storage', code: `auth_session_storage_${stage}`, retryable: true,
      userMessage: FULL_SWING_AUTH_STORAGE_ERROR_MESSAGE
    });
    this.name = 'FullSwingAuthSessionStorageError';
    this.stage = stage;
  }
}

const reportPersistenceFailure = (stage: FullSwingAuthPersistenceStage): void => {
  console.error('[FullSwingAuthSessionStorage] session persistence failed', {stage});

  try {
    track('fullswing_auth_session_storage_failed', {stage});

  } catch {
    console.error('[FullSwingAuthSessionStorage] telemetry reporting failed', {stage});
  }
};

const reportCleanupFailure = (operations: FullSwingAuthCleanupOperation[]): void => {
  console.error('[FullSwingAuthSessionStorage] session cleanup failed', {operations});

  try {
    track('fullswing_auth_session_cleanup_failed', {operations});

  } catch {
    console.error('[FullSwingAuthSessionStorage] cleanup telemetry reporting failed', {operations});
  }
};

/**
 * Persists a complete FullSwing auth record before Context can publish it.
 * The bay credential is written last because it is the capability that can
 * reopen the live player stream after a cold start.
 */
export const persistFullSwingAuthSession = async (params: PersistFullSwingAuthSessionParams): Promise<void> => {
  const {serializedSession, lastActiveTime, backend, bayPlayerJwt} = params;
  const writes: Array<{ stage: Exclude<FullSwingAuthPersistenceStage, 'credential'>, run: () => Promise<void> }> = [
    {stage: 'session', run: () => AsyncStorage.setItem(SESSION_STORAGE_KEY, serializedSession)},
    {stage: 'last-active-time', run: () => AsyncStorage.setItem(LAST_ACTIVE_TIME_STORAGE_KEY, lastActiveTime)},
    {stage: 'backend', run: () => AsyncStorage.setItem(SESSION_BACKEND_STORAGE_KEY, backend)}
  ];

  for (const write of writes) {
    try {
      await write.run();

    } catch {
      reportPersistenceFailure(write.stage);
      throw new FullSwingAuthSessionStorageError(write.stage);
    }
  }

  try {
    await persistBayPlayerJwt(bayPlayerJwt);

  } catch (error) {
    // The credential store already emits sanitized telemetry and supplies a
    // stable user-facing error. Preserve that contract for callers.
    reportPersistenceFailure('credential');
    throw error;
  }
};

/**
 * Best-effort compensating cleanup. Every operation is attempted even if one
 * storage key cannot be removed, and callers receive the failed operations so
 * they can expose an explicit credential error state instead of claiming the
 * device is logged out cleanly.
 */
export const clearFullSwingAuthSession = async (): Promise<FullSwingAuthCleanupOperation[]> => {
  const cleanupOperations: Array<{ operation: FullSwingAuthCleanupOperation; run: () => Promise<void> }> = [
    {operation: 'credential', run: () => persistBayPlayerJwt('')},
    {operation: 'session', run: () => AsyncStorage.removeItem(SESSION_STORAGE_KEY)},
    {operation: 'last-active-time', run: () => AsyncStorage.removeItem(LAST_ACTIVE_TIME_STORAGE_KEY)},
    {operation: 'backend', run: () => AsyncStorage.removeItem(SESSION_BACKEND_STORAGE_KEY)},
    {operation: 'session-id', run: () => AsyncStorage.removeItem(FULL_SWING_SESSION_ID_STORAGE_KEY)},
    {operation: 'confirmation', run: () => clearPersistedActivePlayerConfirmation()}
  ];
  const results = await Promise.allSettled(cleanupOperations.map(({run}) => run()));
  const failedOperations = results.flatMap((result, index) => result.status === 'rejected' ? [cleanupOperations[index].operation] : []);

  if (failedOperations.length > 0) reportCleanupFailure(failedOperations);

  return failedOperations;
};
